import * as THREE from 'three';
import { Brush, Evaluator, SUBTRACTION } from 'three-bvh-csg';
import { mergeVertices, toCreasedNormals } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createHandleProfile } from './configurator-handle-profile';
import {
  buildHandleTransitionCurve,
  deriveDoorGeometry,
  evaluateHandleTransition,
  evaluateHandleTransitionTangent,
  type HandleTransitionCurve,
} from './door-model';
import type { ConfiguratorState } from './configurator-types';

const CSG_SURFACE_EPSILON_MM = 0.05;
const CUTTER_PATH_CLEARANCE_MM = 0.5;
const CUTTER_PATH_SEGMENTS = 96;
const CUTTER_PROFILE_SEGMENTS = 12;
const CUTTER_END_CAP_SEGMENTS = 10;
const CUTTER_END_CAP_RADIUS_RATIO = 0.5;
const SURFACE_CREASE_ANGLE = THREE.MathUtils.degToRad(30);
const CHAMFER_CORNER_SEGMENTS = 18;

/**
 * Builds the finished CSG surface in the same scaled coordinate system used by
 * the interactive preview. This module intentionally has no React, DOM, canvas,
 * presentation-material, renderer, or controls dependency, so document generation
 * can reuse the exact product surface on the server.
 */
export function createDoorSurfaceGeometry(config: ConfiguratorState): THREE.BufferGeometry {
  const requestedGeometry = deriveDoorGeometry(config);
  const mirrorAcrossX =
    requestedGeometry.variant === 'edge-terminated' && requestedGeometry.handle.openLeft;
  // The left-open CSG subtraction loses internal profile faces in three-bvh-csg.
  // Build the proven right-open surface and mirror the finished mesh instead, so
  // both selectable sides are geometrically identical rather than merely similar.
  const surfaceConfig = mirrorAcrossX
    ? { ...config, handle: { ...config.handle, openSide: 'right' as const } }
    : config;
  const geometry = deriveDoorGeometry(surfaceConfig);
  const maxDimension = Math.max(surfaceConfig.widthMm, surfaceConfig.heightMm);
  const scale = 4.2 / maxDimension;
  const width = surfaceConfig.widthMm * scale;
  const height = surfaceConfig.heightMm * scale;
  const depth = surfaceConfig.thicknessMm * scale;
  const surfaceEpsilon = CSG_SURFACE_EPSILON_MM * scale;
  const cornerRadius = surfaceConfig.edgeTreatment.cornerRadiusMm * scale;
  const edgeSize = surfaceConfig.edgeTreatment.sizeMm * scale;

  const baseGeometry = createSlabGeometry(
    width,
    height,
    depth,
    cornerRadius,
    surfaceConfig.edgeTreatment.profile,
    edgeSize,
    surfaceEpsilon,
  );
  baseGeometry.deleteAttribute('uv');
  baseGeometry.clearGroups();

  const cutterDepth = Math.min(
    geometry.handle.depthMm * scale,
    Math.max(surfaceEpsilon, depth - surfaceEpsilon),
  );
  const profileWidth = geometry.handle.grooveWidthMm * scale;
  const pathClearance = Math.max(profileWidth, CUTTER_PATH_CLEARANCE_MM * scale);
  const path = createHandleCurve(surfaceConfig, scale, pathClearance);
  const csgMaterial = new THREE.MeshBasicMaterial();
  let pocketGeometry: THREE.BufferGeometry | undefined;
  let profileGeometry: THREE.BufferGeometry | undefined;
  try {
    pocketGeometry = createHandlePocketCutterGeometry(
      path,
      CUTTER_PATH_SEGMENTS,
      cutterDepth,
      depth / 2,
      surfaceEpsilon,
      geometry.variant === 'full-edge' ? height / 2 : undefined,
    );
    profileGeometry = createSweptHandleProfileCutterGeometry(
      path,
      CUTTER_PATH_SEGMENTS,
      CUTTER_PROFILE_SEGMENTS,
      profileWidth,
      cutterDepth,
      depth / 2,
      surfaceEpsilon,
      {
        roundStart: geometry.handle.capLeft,
        roundEnd: geometry.handle.capRight,
      },
    );
  } catch (error) {
    pocketGeometry?.dispose();
    profileGeometry?.dispose();
    baseGeometry.dispose();
    csgMaterial.dispose();
    throw error;
  }
  if (!pocketGeometry || !profileGeometry) {
    baseGeometry.dispose();
    csgMaterial.dispose();
    throw new Error('Handle cutter construction did not produce geometry.');
  }
  pocketGeometry.clearGroups();
  profileGeometry.clearGroups();

  let resultGeometry: THREE.BufferGeometry | null = null;
  try {
    const baseBrush = new Brush(baseGeometry, csgMaterial);
    const pocketBrush = new Brush(pocketGeometry, csgMaterial);
    const profileBrush = new Brush(profileGeometry, csgMaterial);
    baseBrush.updateMatrixWorld(true);
    pocketBrush.updateMatrixWorld(true);
    profileBrush.updateMatrixWorld(true);
    const evaluator = new Evaluator();
    evaluator.attributes = ['position', 'normal'];
    evaluator.useGroups = false;
    const pocketResult = evaluator.evaluate(baseBrush, pocketBrush, SUBTRACTION);
    const pocketedGeometry = pocketResult.geometry;
    if (pocketedGeometry === baseGeometry || !isUsableGeometry(pocketedGeometry)) {
      if (pocketedGeometry !== baseGeometry) pocketedGeometry.dispose();
      throw new Error('CSG returned an empty or invalid pocket.');
    }
    pocketResult.updateMatrixWorld(true);
    const profileResult = evaluator.evaluate(pocketResult, profileBrush, SUBTRACTION);
    const candidate = profileResult.geometry;
    if (candidate === pocketedGeometry || !isUsableGeometry(candidate)) {
      if (candidate !== pocketedGeometry) candidate.dispose();
      pocketedGeometry.dispose();
      throw new Error('CSG returned an empty or invalid handle profile.');
    }
    resultGeometry = candidate;
    pocketedGeometry.dispose();
  } catch (error) {
    resultGeometry?.dispose();
    baseGeometry.dispose();
    csgMaterial.dispose();
    const reason = error instanceof Error ? error.message : 'Unknown CSG error.';
    throw new Error(`Cove subtraction failed: ${reason}`);
  } finally {
    pocketGeometry.dispose();
    profileGeometry.dispose();
  }

  baseGeometry.dispose();
  csgMaterial.dispose();
  if (mirrorAcrossX) mirrorGeometryAcrossX(resultGeometry);
  resultGeometry.scale(1 / scale, 1 / scale, 1 / scale);
  const shadedGeometry = toCreasedNormals(resultGeometry, SURFACE_CREASE_ANGLE);
  shadedGeometry.scale(scale, scale, scale);
  if (shadedGeometry !== resultGeometry) resultGeometry.dispose();
  return shadedGeometry;
}

function mirrorGeometryAcrossX(geometry: THREE.BufferGeometry) {
  geometry.applyMatrix4(new THREE.Matrix4().makeScale(-1, 1, 1));

  const index = geometry.getIndex();
  if (index) {
    for (let triangle = 0; triangle + 2 < index.count; triangle += 3) {
      const middle = index.getX(triangle + 1);
      index.setX(triangle + 1, index.getX(triangle + 2));
      index.setX(triangle + 2, middle);
    }
    index.needsUpdate = true;
  } else {
    for (const attribute of Object.values(geometry.attributes)) {
      for (let triangle = 0; triangle + 2 < attribute.count; triangle += 3) {
        for (let component = 0; component < attribute.itemSize; component += 1) {
          const middle = attribute.getComponent(triangle + 1, component);
          attribute.setComponent(
            triangle + 1,
            component,
            attribute.getComponent(triangle + 2, component),
          );
          attribute.setComponent(triangle + 2, component, middle);
        }
      }
      attribute.needsUpdate = true;
    }
  }

  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

export function createSlabGeometry(
  width: number,
  height: number,
  depth: number,
  cornerRadius: number,
  profile: ConfiguratorState['edgeTreatment']['profile'],
  edgeSize: number,
  epsilon: number,
) {
  if (profile === 'square' || edgeSize <= epsilon) {
    const shape = createRoundedRectangle(width, height, cornerRadius, epsilon);
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth,
      bevelEnabled: false,
      curveSegments: 18,
      steps: 1,
    });
    geometry.translate(0, 0, -depth / 2);
    return geometry;
  }

  const bevel = Math.min(
    edgeSize,
    Math.max(0, depth / 2 - epsilon),
    Math.max(0, width / 2 - epsilon),
    Math.max(0, height / 2 - epsilon),
  );
  if (profile === 'chamfer') {
    return createChamferedSlabGeometry(width, height, depth, cornerRadius, bevel, epsilon);
  }

  const shape = createRoundedRectangle(width, height, cornerRadius, epsilon);
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(epsilon, depth - bevel * 2),
    bevelEnabled: true,
    bevelSegments: 5,
    bevelSize: bevel,
    bevelThickness: bevel,
    // Three.js expands beveled contours by default. An inward offset preserves the
    // configured finished X/Y bounding box for both chamfers and round-overs.
    bevelOffset: -bevel,
    curveSegments: 18,
    steps: 1,
  });
  geometry.translate(0, 0, -depth / 2 + bevel);
  return geometry;
}

function createChamferedSlabGeometry(
  width: number,
  height: number,
  depth: number,
  cornerRadius: number,
  bevel: number,
  epsilon: number,
) {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const outerRadius = Math.max(0, Math.min(cornerRadius, halfWidth, halfHeight));
  const innerWidth = Math.max(epsilon, width - bevel * 2);
  const innerHeight = Math.max(epsilon, height - bevel * 2);
  const innerRadius = Math.max(0, Math.min(outerRadius - bevel, innerWidth / 2, innerHeight / 2));
  const outerContour = createRoundedRectangleContour(
    width,
    height,
    outerRadius,
    CHAMFER_CORNER_SEGMENTS,
    true,
  );
  const innerContour = createRoundedRectangleContour(
    innerWidth,
    innerHeight,
    innerRadius,
    CHAMFER_CORNER_SEGMENTS,
    true,
  );
  const capContour = createRoundedRectangleContour(
    innerWidth,
    innerHeight,
    innerRadius,
    CHAMFER_CORNER_SEGMENTS,
    false,
  );
  const positions: number[] = [];
  const outerBackZ = -depth / 2 + bevel;
  const outerFrontZ = depth / 2 - bevel;
  const backZ = -depth / 2;
  const frontZ = depth / 2;
  const atZ = (point: THREE.Vector2, z: number) => new THREE.Vector3(point.x, point.y, z);

  for (let index = 0; index < outerContour.length; index += 1) {
    const next = (index + 1) % outerContour.length;
    const outerBack = atZ(outerContour[index], outerBackZ);
    const outerBackNext = atZ(outerContour[next], outerBackZ);
    const outerFront = atZ(outerContour[index], outerFrontZ);
    const outerFrontNext = atZ(outerContour[next], outerFrontZ);
    const innerBack = atZ(innerContour[index], backZ);
    const innerBackNext = atZ(innerContour[next], backZ);
    const innerFront = atZ(innerContour[index], frontZ);
    const innerFrontNext = atZ(innerContour[next], frontZ);

    appendGeometryQuad(positions, outerBack, outerBackNext, outerFrontNext, outerFront);
    appendGeometryQuad(positions, outerFront, outerFrontNext, innerFrontNext, innerFront);
    appendGeometryQuad(positions, outerBack, innerBack, innerBackNext, outerBackNext);
  }

  const capFaces = THREE.ShapeUtils.triangulateShape(capContour, []);
  for (const [a, b, c] of capFaces) {
    appendGeometryTriangle(
      positions,
      atZ(capContour[a], frontZ),
      atZ(capContour[b], frontZ),
      atZ(capContour[c], frontZ),
    );
    appendGeometryTriangle(
      positions,
      atZ(capContour[c], backZ),
      atZ(capContour[b], backZ),
      atZ(capContour[a], backZ),
    );
  }

  const rawGeometry = new THREE.BufferGeometry();
  rawGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const geometry = mergeVertices(rawGeometry, Math.max(Number.EPSILON, epsilon * 0.01));
  rawGeometry.dispose();
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createRoundedRectangleContour(
  width: number,
  height: number,
  radius: number,
  cornerSegments: number,
  preserveCornerSamples: boolean,
) {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const safeRadius = Math.max(0, Math.min(radius, halfWidth, halfHeight));
  if (safeRadius === 0 && !preserveCornerSamples) {
    return [
      new THREE.Vector2(-halfWidth, -halfHeight),
      new THREE.Vector2(halfWidth, -halfHeight),
      new THREE.Vector2(halfWidth, halfHeight),
      new THREE.Vector2(-halfWidth, halfHeight),
    ];
  }

  const points: THREE.Vector2[] = [];
  const corners = [
    { x: halfWidth - safeRadius, y: -halfHeight + safeRadius, start: -Math.PI / 2 },
    { x: halfWidth - safeRadius, y: halfHeight - safeRadius, start: 0 },
    { x: -halfWidth + safeRadius, y: halfHeight - safeRadius, start: Math.PI / 2 },
    { x: -halfWidth + safeRadius, y: -halfHeight + safeRadius, start: Math.PI },
  ];
  for (const corner of corners) {
    for (let segment = 0; segment <= cornerSegments; segment += 1) {
      const angle = corner.start + (segment / cornerSegments) * (Math.PI / 2);
      points.push(
        new THREE.Vector2(
          corner.x + Math.cos(angle) * safeRadius,
          corner.y + Math.sin(angle) * safeRadius,
        ),
      );
    }
  }
  return points;
}

function appendGeometryQuad(
  positions: number[],
  a: THREE.Vector3,
  b: THREE.Vector3,
  c: THREE.Vector3,
  d: THREE.Vector3,
) {
  appendGeometryTriangle(positions, a, b, d);
  appendGeometryTriangle(positions, b, c, d);
}

function appendGeometryTriangle(
  positions: number[],
  a: THREE.Vector3,
  b: THREE.Vector3,
  c: THREE.Vector3,
) {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abz = b.z - a.z;
  const acx = c.x - a.x;
  const acy = c.y - a.y;
  const acz = c.z - a.z;
  const crossX = aby * acz - abz * acy;
  const crossY = abz * acx - abx * acz;
  const crossZ = abx * acy - aby * acx;
  if (crossX * crossX + crossY * crossY + crossZ * crossZ <= Number.EPSILON ** 2) return;
  positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
}

function createRoundedRectangle(width: number, height: number, radius: number, epsilon: number) {
  const shape = new THREE.Shape();
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const safeRadius = Math.max(0, Math.min(radius, halfWidth, halfHeight));
  if (safeRadius <= epsilon) {
    shape.moveTo(-halfWidth, -halfHeight);
    shape.lineTo(halfWidth, -halfHeight);
    shape.lineTo(halfWidth, halfHeight);
    shape.lineTo(-halfWidth, halfHeight);
    shape.closePath();
    return shape;
  }

  shape.moveTo(-halfWidth + safeRadius, -halfHeight);
  shape.lineTo(halfWidth - safeRadius, -halfHeight);
  shape.quadraticCurveTo(halfWidth, -halfHeight, halfWidth, -halfHeight + safeRadius);
  shape.lineTo(halfWidth, halfHeight - safeRadius);
  shape.quadraticCurveTo(halfWidth, halfHeight, halfWidth - safeRadius, halfHeight);
  shape.lineTo(-halfWidth + safeRadius, halfHeight);
  shape.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth, halfHeight - safeRadius);
  shape.lineTo(-halfWidth, -halfHeight + safeRadius);
  shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth + safeRadius, -halfHeight);
  shape.closePath();
  return shape;
}

class QuinticHandleTransitionCurve3 extends THREE.Curve<THREE.Vector3> {
  constructor(private readonly transition: HandleTransitionCurve) {
    super();
  }

  getPoint(t: number, target = new THREE.Vector3()) {
    const point = evaluateHandleTransition(this.transition, t);
    return target.set(point.x, point.y, 0);
  }

  getTangent(t: number, target = new THREE.Vector3()) {
    const tangent = evaluateHandleTransitionTangent(this.transition, t);
    return target.set(tangent.x, tangent.y, 0).normalize();
  }
}

export function createHandleCurve(
  config: ConfiguratorState,
  scale: number,
  profileClearance: number,
) {
  const geometry = deriveDoorGeometry(config);
  const width = config.widthMm * scale;
  const height = config.heightMm * scale;
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const toX = (millimetres: number) => millimetres * scale - halfWidth;
  const top = halfHeight;
  const trackY = top - geometry.handle.faceWidthMm * scale;
  const transition = geometry.handle.transitionLengthMm * scale;
  const outside = profileClearance + CUTTER_PATH_CLEARANCE_MM * scale;
  const path = new THREE.CurvePath<THREE.Vector3>();

  const addLeftCap = (startX: number) => {
    const transitionCurve = buildHandleTransitionCurve(
      { x: startX, y: top },
      { x: startX + transition, y: trackY },
      geometry.handle.transitionRoundness,
    );
    const start = new THREE.Vector3(transitionCurve.start.x, transitionCurve.start.y, 0);
    path.add(new THREE.LineCurve3(new THREE.Vector3(startX - outside, top, 0), start));
    path.add(new QuinticHandleTransitionCurve3(transitionCurve));
  };
  const addRightCap = (endX: number) => {
    const transitionCurve = buildHandleTransitionCurve(
      { x: endX - transition, y: trackY },
      { x: endX, y: top },
      geometry.handle.transitionRoundness,
    );
    path.add(new QuinticHandleTransitionCurve3(transitionCurve));
    path.add(
      new THREE.LineCurve3(
        new THREE.Vector3(transitionCurve.end.x, transitionCurve.end.y, 0),
        new THREE.Vector3(endX + outside, top, 0),
      ),
    );
  };

  const startX = toX(geometry.handle.startMm);
  const endX = toX(geometry.handle.endMm);
  const flatStart = geometry.handle.capLeft ? startX + transition : startX - outside;
  const flatEnd = geometry.handle.capRight ? endX - transition : endX + outside;

  if (geometry.handle.capLeft) addLeftCap(startX);
  if (flatEnd > flatStart) {
    path.add(
      new THREE.LineCurve3(
        new THREE.Vector3(flatStart, trackY, 0),
        new THREE.Vector3(flatEnd, trackY, 0),
      ),
    );
  }
  if (geometry.handle.capRight) addRightCap(endX);

  return path;
}

/**
 * Removes the broad field above the handle boundary down to the configured Z.
 * This is the defining feature visible in the supplied 3D reference: the handle
 * is a deep recessed band, not a thin decorative groove drawn on the front face.
 */
export function createHandlePocketCutterGeometry(
  curve: THREE.Curve<THREE.Vector3>,
  pathSegments: number,
  cutDepth: number,
  frontZ: number,
  frontEpsilon: number,
  topBoundaryY?: number,
) {
  const pathSteps = Math.max(2, Math.floor(pathSegments));
  if (
    !Number.isFinite(cutDepth) ||
    !Number.isFinite(frontZ) ||
    !Number.isFinite(frontEpsilon) ||
    (topBoundaryY !== undefined && !Number.isFinite(topBoundaryY)) ||
    cutDepth <= 0 ||
    frontEpsilon <= 0 ||
    curve.getLength() <= 0
  ) {
    throw new Error('Invalid handle-pocket dimensions.');
  }

  const shape = createHandlePocketShape(curve, pathSteps, undefined, topBoundaryY);

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: cutDepth + frontEpsilon * 2,
    bevelEnabled: false,
    curveSegments: 1,
    steps: 1,
  });
  geometry.translate(0, 0, frontZ - cutDepth - frontEpsilon);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createHandlePocketShape(
  curve: THREE.Curve<THREE.Vector3>,
  pathSegments: number,
  clip?: { left: number; right: number; top: number },
  topBoundaryY?: number,
) {
  const pathPoints = curve.getSpacedPoints(Math.max(2, Math.floor(pathSegments)));
  const rawTop = Math.max(...pathPoints.map(point => point.y));
  const rawBottom = Math.min(...pathPoints.map(point => point.y));
  const rawLeft = Math.min(...pathPoints.map(point => point.x));
  const rawRight = Math.max(...pathPoints.map(point => point.x));
  const top = topBoundaryY ?? clip?.top ?? rawTop;
  const left = clip ? THREE.MathUtils.clamp(rawLeft, clip.left, clip.right) : rawLeft;
  const right = clip ? THREE.MathUtils.clamp(rawRight, clip.left, clip.right) : rawRight;
  if (top - rawBottom <= 1e-9) {
    throw new Error('Handle pocket boundary has no height.');
  }
  const toPoint = (point: THREE.Vector3) =>
    new THREE.Vector2(
      clip ? THREE.MathUtils.clamp(point.x, left, right) : point.x,
      clip ? Math.min(point.y, top) : point.y,
    );
  const first = toPoint(pathPoints[0]);
  const shape = new THREE.Shape();
  shape.moveTo(first.x, first.y);
  for (let index = 1; index < pathPoints.length; index += 1) {
    const point = toPoint(pathPoints[index]);
    shape.lineTo(point.x, point.y);
  }
  shape.lineTo(right, top);
  shape.lineTo(left, top);
  shape.closePath();
  return shape;
}

/**
 * Sweeps a deep asymmetric hook profile along the visible handle boundary. The
 * profile is deliberately labelled schematic in the UI: the TCN gives the two
 * XY/Z passes and tool id 1009, but its tool-table profile is not embedded.
 */
export function createSweptHandleProfileCutterGeometry(
  curve: THREE.Curve<THREE.Vector3>,
  pathSegments: number,
  profileSegments: number,
  profileWidth: number,
  cutDepth: number,
  frontZ: number,
  frontEpsilon: number,
  { roundStart = false, roundEnd = false }: { roundStart?: boolean; roundEnd?: boolean } = {},
) {
  const pathSteps = Math.max(2, Math.floor(pathSegments));
  const curveSteps = Math.max(4, Math.floor(profileSegments));
  const curveLength = curve.getLength();
  if (
    !Number.isFinite(profileWidth) ||
    !Number.isFinite(cutDepth) ||
    !Number.isFinite(frontZ) ||
    !Number.isFinite(frontEpsilon) ||
    profileWidth <= 0 ||
    cutDepth <= 0 ||
    frontEpsilon <= 0 ||
    curveLength <= 0
  ) {
    throw new Error('Invalid swept-handle dimensions.');
  }

  const profile = createHandleProfile(profileWidth, cutDepth, frontZ, frontEpsilon, curveSteps);
  const profileCount = profile.length;
  const requestedCapRadius = Math.max(frontEpsilon * 2, profileWidth * CUTTER_END_CAP_RADIUS_RATIO);
  const capRadius = Math.min(requestedCapRadius, curveLength * 0.24);
  const sweepStart = roundStart ? capRadius / curveLength : 0;
  const sweepEnd = roundEnd ? 1 - capRadius / curveLength : 1;
  if (sweepEnd <= sweepStart) throw new Error('Rounded handle caps leave no sweep length.');

  const vertices: number[] = [];
  const indices: number[] = [];
  const pathFrames: Array<{
    point: THREE.Vector3;
    tangent: THREE.Vector2;
    normal: THREE.Vector2;
  }> = [];
  let previousNormal: THREE.Vector2 | null = null;

  for (let pathIndex = 0; pathIndex <= pathSteps; pathIndex += 1) {
    const t = THREE.MathUtils.lerp(sweepStart, sweepEnd, pathIndex / pathSteps);
    const point = curve.getPointAt(t);
    const tangent3 = curve.getTangentAt(t);
    const tangent = new THREE.Vector2(tangent3.x, tangent3.y);
    if (tangent.lengthSq() === 0) throw new Error('Handle path contains a zero tangent.');
    tangent.normalize();
    const normal = new THREE.Vector2(-tangent.y, tangent.x);
    if (previousNormal && normal.dot(previousNormal) < 0) normal.negate();
    previousNormal = normal.clone();
    pathFrames.push({ point, tangent, normal });

    for (const profilePoint of profile) {
      vertices.push(
        point.x + normal.x * profilePoint.x,
        point.y + normal.y * profilePoint.x,
        profilePoint.y,
      );
    }
  }

  const connectRings = (fromRingOffset: number, toRingOffset: number) => {
    for (let profileIndex = 0; profileIndex < profileCount; profileIndex += 1) {
      const profileNext = (profileIndex + 1) % profileCount;
      const a = fromRingOffset + profileIndex;
      const b = toRingOffset + profileIndex;
      const c = toRingOffset + profileNext;
      const d = fromRingOffset + profileNext;
      indices.push(a, d, b, d, c, b);
    }
  };

  for (let pathIndex = 0; pathIndex < pathSteps; pathIndex += 1) {
    connectRings(pathIndex * profileCount, (pathIndex + 1) * profileCount);
  }

  const capTriangles = THREE.ShapeUtils.triangulateShape(profile, []);
  const endRingOffset = pathSteps * profileCount;
  if (!roundStart) {
    for (const [a, b, c] of capTriangles) indices.push(c, b, a);
  }
  if (!roundEnd) {
    for (const [a, b, c] of capTriangles) {
      indices.push(endRingOffset + a, endRingOffset + b, endRingOffset + c);
    }
  }

  // The cap collapses around the profile's area centroid, producing one smooth
  // spoon-shaped pole instead of the former planar, perpendicular end wall.
  let signedDoubleArea = 0;
  const profileCenter = new THREE.Vector2();
  for (let index = 0; index < profileCount; index += 1) {
    const current = profile[index];
    const next = profile[(index + 1) % profileCount];
    const cross = current.x * next.y - next.x * current.y;
    signedDoubleArea += cross;
    profileCenter.x += (current.x + next.x) * cross;
    profileCenter.y += (current.y + next.y) * cross;
  }
  if (Math.abs(signedDoubleArea) > Number.EPSILON) {
    profileCenter.multiplyScalar(1 / (3 * signedDoubleArea));
  } else {
    new THREE.Box2().setFromPoints(profile).getCenter(profileCenter);
  }

  const appendRoundedCap = (
    frame: (typeof pathFrames)[number],
    baseRingOffset: number,
    atStart: boolean,
  ) => {
    const outward = frame.tangent.clone().multiplyScalar(atStart ? -1 : 1);
    let innerRingOffset = baseRingOffset;

    for (let capIndex = 1; capIndex < CUTTER_END_CAP_SEGMENTS; capIndex += 1) {
      const progress = capIndex / CUTTER_END_CAP_SEGMENTS;
      const offset = Math.sin(progress * Math.PI * 0.5) * capRadius;
      const ringScale = Math.cos(progress * Math.PI * 0.5);
      const ringOffset = vertices.length / 3;
      const ringCenterX = frame.point.x + outward.x * offset;
      const ringCenterY = frame.point.y + outward.y * offset;

      for (const profilePoint of profile) {
        const localX = profileCenter.x + (profilePoint.x - profileCenter.x) * ringScale;
        const localZ = profileCenter.y + (profilePoint.y - profileCenter.y) * ringScale;
        vertices.push(
          ringCenterX + frame.normal.x * localX,
          ringCenterY + frame.normal.y * localX,
          localZ,
        );
      }

      if (atStart) connectRings(ringOffset, innerRingOffset);
      else connectRings(innerRingOffset, ringOffset);
      innerRingOffset = ringOffset;
    }

    const tipOffset = vertices.length / 3;
    vertices.push(
      frame.point.x + outward.x * capRadius + frame.normal.x * profileCenter.x,
      frame.point.y + outward.y * capRadius + frame.normal.y * profileCenter.x,
      profileCenter.y,
    );
    for (let profileIndex = 0; profileIndex < profileCount; profileIndex += 1) {
      const profileNext = (profileIndex + 1) % profileCount;
      const current = innerRingOffset + profileIndex;
      const next = innerRingOffset + profileNext;
      if (atStart) indices.push(tipOffset, next, current);
      else indices.push(tipOffset, current, next);
    }
  };

  if (roundStart) appendRoundedCap(pathFrames[0], 0, true);
  if (roundEnd) appendRoundedCap(pathFrames[pathFrames.length - 1], endRingOffset, false);

  if (pathFrames.length !== pathSteps + 1) {
    throw new Error('Handle sweep frame construction failed.');
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function isUsableGeometry(geometry: THREE.BufferGeometry) {
  const positions = geometry.getAttribute('position');
  if (!positions || positions.count < 3) return false;
  for (let index = 0; index < positions.array.length; index += 1) {
    if (!Number.isFinite(positions.array[index])) return false;
  }
  geometry.computeBoundingBox();
  const bounds = geometry.boundingBox;
  if (!bounds || bounds.isEmpty()) return false;
  const size = bounds.getSize(new THREE.Vector3());
  return Number.isFinite(size.x) && Number.isFinite(size.y) && Number.isFinite(size.z);
}
